import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  formatDisplayValue,
  getParameterStatus,
} from "../../utils/smartRoomStatusRanges";

/**
 * Reusable environmental parameter card used by Indoor and Outdoor dashboards.
 */
const ParameterCard = ({
  icon,
  name,
  value,
  unit = "",
  rangeKey = null,
  info = "",
  showProgress = true,
  statusOverride = null,
  colorOverride = null,
}) => {
  const resolved = getParameterStatus(rangeKey, value);
  const status = statusOverride || resolved.status;
  const color = colorOverride || resolved.color;
  const progress = resolved.progress;
  const numericDisplay = formatDisplayValue(value);
  const displayValue =
    numericDisplay !== "—"
      ? numericDisplay
      : value !== null && value !== undefined && value !== ""
      ? String(value)
      : "—";

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "rgba(15, 23, 42, 0.08)",
        bgcolor: "#fff",
        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
        overflow: "hidden",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
          boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <CardContent
        sx={{
          p: 1.75,
          "&:last-child": { pb: 1.75 },
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                bgcolor: `${color}18`,
                color,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  fontSize: 13,
                  letterSpacing: 0.1,
                }}
                noWrap
              >
                {name}
              </Typography>
              {info ? (
                <Tooltip title={info} arrow>
                  <InfoOutlinedIcon
                    sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }}
                  />
                </Tooltip>
              ) : null}
            </Box>
          </Box>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 0.9,
              py: 0.35,
              borderRadius: 999,
              bgcolor: `${color}14`,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: color,
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color, fontSize: 11, lineHeight: 1 }}
            >
              {status}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: 22, md: 24 },
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "text.primary",
            }}
          >
            {displayValue}
          </Typography>
          {unit && displayValue !== "—" ? (
            <Typography
              component="span"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              {unit}
            </Typography>
          ) : null}
        </Box>

        {showProgress && rangeKey ? (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 5,
              borderRadius: 999,
              bgcolor: "rgba(15, 23, 42, 0.06)",
              "& .MuiLinearProgress-bar": {
                bgcolor: color,
                borderRadius: 999,
              },
            }}
          />
        ) : (
          <Box sx={{ height: 5 }} />
        )}
      </CardContent>
    </Card>
  );
};

ParameterCard.propTypes = {
  icon: PropTypes.node,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  rangeKey: PropTypes.string,
  info: PropTypes.string,
  showProgress: PropTypes.bool,
  statusOverride: PropTypes.string,
  colorOverride: PropTypes.string,
};

export default ParameterCard;
